<template>
  <div>
    <FilterPanel v-model="filter" :with-additionals="true" @refresh="refresh" />
    <ReportGraph
      v-if="!!chartData.length && !isMobileViewport"
      :data="chartData"
      :axes="axes"
    />
    <ReportPie v-if="!!pieData.length && !isMobileViewport" :data="pieData" />
    <ReportTable
      v-if="!!responseData.data.length"
      :table-data="responseData"
      :group="filter.group_by"
      @download="download"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onUnmounted } from 'vue'

import useResize from '@/hooks/useResize'
import useRouter from '@/hooks/useRouter'

import NaRespondedJob from '@/utils/responded_job'
import { IRespondedJob } from '@/interfaces/respondedJobs'
import { exportJson2Excel, exportCSVFile } from '@/utils/excel'
import { formatJson } from '@/utils'
import { ElMessage } from 'element-plus'

import FilterPanel from './FilterPanel.vue'
import ReportTable from '@/components/Reports/ReportTable.vue'
import ReportGraph from '@/components/Reports/ReportGraph.vue'
import ReportPie from '@/components/Reports/ReportPie.vue'

const CHARTS: Record<string, boolean> = {
  date: true
}

const BLOCKED_PIE: Record<string, boolean> = {
  creature: true,
  referer_host: true,
  website: true
}

const CONVERSION_METRICS = ['gross_revenue', 'gross_cpm', 'profit']

export default defineComponent({
  name: 'TabFull',
  components: {
    ReportTable,
    ReportGraph,
    ReportPie,
    FilterPanel
  },
  props: {
    jid: {
      type: String,
      default: null
    },
    modelValue: {
      type: Boolean,
      default: false
    }
  },
  setup(props, context) {
    const { isMobileViewport } = useResize()
    const chartData = ref([])
    const pieData = ref([])
    const defaultResponse = { tableColumns: [], data: [], headers: {} }
    const responseData = ref({ ...defaultResponse })
    const filter = ref({})
    const { route, router } = useRouter()
    const axes = [
      { title: 'rCPM', value: 'real_cpm' },
      { title: 'Cost', value: 'broker_income' },
      { title: 'Impressions', value: 'impressions' }
    ]

    const job = new NaRespondedJob(
      (error: string, args: any) => {
        context.emit('update:modelValue', false)
        filter.value = { ...filter.value, ...args }
        ElMessage.error(error)
      },
      (id: string) => {
        router.replace({
          name: 'reports-tab-result',
          params: { tab: 'full', jid: id }
        })
      },
      () => {
        context.emit('update:modelValue', false)
        router.replace({ name: 'reports-tab', params: { tab: 'full' } })
      }
    )

    const fillJobData = (job: IRespondedJob) => {
      context.emit('update:modelValue', false)

      if (Array.isArray(job.attributes.result)) {
        return
      }
      if (job.attributes.args.group_by === 'one_min') {
        return
      }
      if (CHARTS[job.attributes.args.group_by]) {
        chartData.value = job.attributes.result.graph
      } else if (!BLOCKED_PIE[job.attributes.args.group_by]) {
        pieData.value = job.attributes.result.graph
      }

      filter.value = { ...job.attributes.args }
      responseData.value = job.attributes.result
      if (
        (responseData.value && responseData.value.data.length < 1) ||
        !responseData.value
      ) {
        ElMessage.error('Empty statistics')
      }
    }

    if (props.jid) {
      context.emit('update:modelValue', true)
      job.setAfterJobCallback(fillJobData)
      job.checkJobWithInterval(props.jid)
    }

    onUnmounted(() => {
      job.destroy()
    })

    const refresh = (newFilter: any) => {
      context.emit('update:modelValue', true)
      chartData.value = []
      pieData.value = []
      responseData.value = { ...defaultResponse }

      const payload = {
        ...newFilter,
        period: `${newFilter.start_at} - ${newFilter.finish_at}`,
        conversion_types: withConversionMetrics(newFilter.conversion_types)
      }

      job.runRespondedJob('type_traforama_report', payload, fillJobData)
    }

    const withConversionMetrics = (conversionTypes: number[] = []) => {
      return conversionTypes.length
        ? [...conversionTypes, ...CONVERSION_METRICS]
        : []
    }

    const download = (format: string) => {
      const data = formatJson(
        responseData.value.tableColumns,
        responseData.value.data
      )
      if (format === 'csv') {
        exportCSVFile(Object.values(responseData.value.headers), data)
      } else {
        exportJson2Excel(Object.values(responseData.value.headers), data)
      }
    }

    return {
      isMobileViewport,
      chartData,
      pieData,
      responseData,
      refresh,
      download,
      filter,
      axes
    }
  }
})
</script>

<style lang="scss"></style>
